import { useCallback, useMemo, useState } from 'react';

/**
 * Constants for property comparison limits
 */
export const MIN_COMPARISON = 2;
export const MAX_COMPARISON = 4;

/**
 * Return type for the usePropertyComparison hook
 */
export interface UsePropertyComparisonReturn {
  /** Set of selected property IDs for comparison */
  selectedIds: Set<string>;
  /** Add a property to comparison if under the limit */
  addToComparison: (id: string) => void;
  /** Remove a property from comparison */
  removeFromComparison: (id: string) => void;
  /** Clear all selected properties */
  clearComparison: () => void;
  /** Check if a property is selected for comparison */
  isSelected: (id: string) => boolean;
  /** Whether more properties can be added (under MAX_COMPARISON) */
  canAddMore: boolean;
  /** Whether comparison is ready (at least MIN_COMPARISON selected) */
  comparisonReady: boolean;
  /** Number of selected properties */
  selectionCount: number;
  /** Toggle a property's selection state */
  toggleSelection: (id: string) => void;
}

/**
 * Custom hook for managing property comparison selection state
 *
 * Provides selection state and helpers for a property comparison feature
 * that allows users to select 2-4 properties for side-by-side comparison.
 *
 * @returns Selection state and control functions
 *
 * @example
 * ```tsx
 * const {
 *   selectedIds,
 *   addToComparison,
 *   removeFromComparison,
 *   isSelected,
 *   comparisonReady,
 * } = usePropertyComparison();
 *
 * // Add a property to comparison
 * addToComparison('property-123');
 *
 * // Check if comparison can be shown
 * if (comparisonReady) {
 *   // Show comparison modal
 * }
 * ```
 */
export function usePropertyComparison(): UsePropertyComparisonReturn {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Add a property to comparison if under the limit
  const addToComparison = useCallback((id: string) => {
    setSelectedIds((prev) => {
      if (prev.size >= MAX_COMPARISON) {
        return prev; // Don't add if at limit
      }
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  // Remove a property from comparison
  const removeFromComparison = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  // Clear all selected properties
  const clearComparison = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  // Check if a property is selected
  const isSelected = useCallback(
    (id: string): boolean => {
      return selectedIds.has(id);
    },
    [selectedIds]
  );

  // Toggle a property's selection state
  const toggleSelection = useCallback(
    (id: string) => {
      if (selectedIds.has(id)) {
        removeFromComparison(id);
      } else {
        addToComparison(id);
      }
    },
    [selectedIds, addToComparison, removeFromComparison]
  );

  // Computed: can add more properties
  const canAddMore = useMemo(
    () => selectedIds.size < MAX_COMPARISON,
    [selectedIds.size]
  );

  // Computed: comparison is ready (enough properties selected)
  const comparisonReady = useMemo(
    () => selectedIds.size >= MIN_COMPARISON,
    [selectedIds.size]
  );

  // Selection count
  const selectionCount = selectedIds.size;

  return {
    selectedIds,
    addToComparison,
    removeFromComparison,
    clearComparison,
    isSelected,
    canAddMore,
    comparisonReady,
    selectionCount,
    toggleSelection,
  };
}

export default usePropertyComparison;
